import Card from '../../components/ui/Card';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../../api/auth';
import { useAuth } from '../../context/AuthContext';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, passwordError } from '../../config/password';
import type { UserRole } from '../../types/index.js';

const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System Admin',
  city_admin:   'Stadt-Admin',
  member:       'Mitglied',
  read_only:    'Lesezugriff',
};

export default function Profil() {
  const { user } = useAuth();

  if (!user) return <div className="p-6">Lädt…</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold">Mein Profil</h1>

      <Card className="border border-gray-200 p-5 space-y-3">
        <h2 className="font-semibold">Meine Daten</h2>
        <dl className="grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
          <dt className="text-gray-500">Name</dt>
          <dd>{user.full_name}</dd>
          <dt className="text-gray-500">E-Mail-Adresse</dt>
          <dd>{user.email}</dd>
          <dt className="text-gray-500">Rolle</dt>
          <dd>{ROLE_LABELS[user.role]}</dd>
          <dt className="text-gray-500">Stadt</dt>
          <dd>{user.city?.name ?? '—'}</dd>
        </dl>
        <p className="text-sm text-gray-500 italic">
          Name, E-Mail-Adresse und Rolle ändert Ihre Administration.
        </p>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    },
  });

  const tooShort = passwordError(newPassword);
  const mismatch = repeatPassword.length > 0 && newPassword !== repeatPassword;
  const canSubmit =
    !!currentPassword &&
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    newPassword === repeatPassword &&
    !mutation.isPending;

  return (
    <Card className="border border-gray-200 p-5 space-y-3">
      <h2 className="font-semibold">Passwort ändern</h2>

      <label className="block">
        <span className="text-sm text-gray-700">Aktuelles Passwort</span>
        <input
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded"
        />
      </label>

      <label className="block">
        <span className="text-sm text-gray-700">Neues Passwort</span>
        <input
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded"
        />
        <span className="text-xs text-gray-500">{PASSWORD_HINT}</span>
      </label>

      <label className="block">
        <span className="text-sm text-gray-700">Neues Passwort wiederholen</span>
        <input
          type="password"
          autoComplete="new-password"
          value={repeatPassword}
          onChange={(e) => setRepeatPassword(e.target.value)}
          className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded"
        />
      </label>

      {tooShort && <div className="text-sm text-red-600">{tooShort}</div>}
      {mismatch && (
        <div className="text-sm text-red-600">Die beiden neuen Passwörter stimmen nicht überein.</div>
      )}
      {mutation.isSuccess && (
        <div className="text-sm text-green-700">
          Passwort geändert. Es gilt ab der nächsten Anmeldung.
        </div>
      )}
      {mutation.error && <div className="text-sm text-red-600">{mutation.error.message}</div>}

      <div className="flex justify-end">
        <button
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          className="btn btn-primary px-3 py-1.5"
        >
          {mutation.isPending ? 'Ändern…' : 'Passwort ändern'}
        </button>
      </div>
    </Card>
  );
}
